import api from "./client";


// ---------- MESAS DE SEPARAÇÃO ----------

export interface MesaSeparacao {
	mesa_separacao_id: number;
	nome: string;
	ean: string;
	horario_corte: string;
	ativo: boolean;
	canal_venda?: Array<{
		canal_venda_id: number;
		nome: string;
		horario_corte?: string;
	}>;
}


export interface CreateMesaSeparacaoDto {
	nome: string;
	horario_corte?: string;
	canal_venda_ids?: number[];
}


export interface UpdateMesaSeparacaoDto {
	nome?: string;
	ean?: string;
	horario_corte?: string;
	canal_venda_ids?: number[];
}


// Buscar mesas de separação com paginação e pesquisa
export const buscarMesasSeparacao = async (
	search?: string,
	offset: number = 0,
): Promise<{
	results: MesaSeparacao[];
	total: { offset: number; total_itens: number };
}> => {
	try {
		const params: any = { offset };
		if (search) params.search = search;

		const response = await api.get("/mesa-separacao", { params });
		return response.data;
	} catch (error) {
		console.error("Erro ao buscar mesas de separação:", error);
		throw error;
	}
};


// Buscar mesa de separação por ID
export const buscarMesaSeparacaoPorId = async (
	id: number,
): Promise<MesaSeparacao> => {
	try {
		const response = await api.get(`/mesa-separacao/${id}`);
		return response.data;
	} catch (error) {
		console.error("Erro ao buscar mesa de separação:", error);
		throw error;
	}
};


// Criar mesa de separação
export const criarMesaSeparacao = async (
	dados: CreateMesaSeparacaoDto,
): Promise<MesaSeparacao> => {
	try {
		const response = await api.post("/mesa-separacao", dados);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao criar mesa de separação:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao criar mesa de separação.");
	}
};


// Atualizar mesa de separação
export const atualizarMesaSeparacao = async (
	id: number,
	dados: UpdateMesaSeparacaoDto,
): Promise<MesaSeparacao> => {
	try {
		const response = await api.patch(`/mesa-separacao/${id}`, dados);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao atualizar mesa de separação:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao atualizar mesa de separação.");
	}
};


// Deletar mesa de separação
export const deletarMesaSeparacao = async (id: number): Promise<void> => {
	try {
		await api.delete(`/mesa-separacao/${id}`);
	} catch (error: any) {
		console.error("Erro ao deletar mesa de separação:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao deletar mesa de separação.");
	}
};


// Toggle ativo/desativo de mesa de separação
export const toggleAtivoMesaSeparacao = async (
	id: number,
): Promise<MesaSeparacao> => {
	try {
		const response = await api.patch(`/mesa-separacao/${id}/toggle-ativo`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao alterar status da mesa de separação:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao alterar status da mesa de separação.");
	}
};


// ---------- HORÁRIOS DOS OPERADORES ----------

export type TipoSeparador =
	| "separador"
	| "separador full"
	| "repositor"
	| "conferidor";


export interface HorarioSeparador {
	horario_separador_id: number;
	usuario_id: number;
	usuario: {
		usuario_id: number;
		responsavel: string;
		usuario: string;
	};
	tipo_separador: TipoSeparador[];
	dias_da_semana: number[]; // 0 = Domingo, 1 = Segunda, ..., 6 = Sábado
	horario_inicio: string; // Formato HH:mm
	horario_fim: string; // Formato HH:mm
	ativo: boolean;
}


export interface CreateHorarioSeparadorDto {
	usuario_id: number;
	tipo_separador: TipoSeparador[];
	dias_da_semana: number[];
	horario_inicio: string;
	horario_fim: string;
	ativo?: boolean;
}


export interface UpdateHorarioSeparadorDto {
	usuario_id?: number;
	tipo_separador?: TipoSeparador[];
	dias_da_semana?: number[];
	horario_inicio?: string;
	horario_fim?: string;
	ativo?: boolean;
}


// Buscar horários dos operadores
export const buscarHorariosSeparadores = async (
	tipoSeparador?: TipoSeparador,
	usuarioId?: number,
): Promise<HorarioSeparador[]> => {
	try {
		const params: any = {};
		if (tipoSeparador) params.tipo_separador = tipoSeparador;
		if (usuarioId) params.usuario_id = usuarioId;

		const response = await api.get("/horario-separador", { params });
		return response.data;
	} catch (error) {
		console.error("Erro ao buscar horários dos operadores:", error);
		throw error;
	}
};


// Buscar horários dos operadores com paginação e pesquisa
export const buscarHorariosSeparadoresPaginado = async (
	search?: string,
	tipoSeparador?: TipoSeparador,
	offset: number = 0,
	limit: number = 50,
): Promise<{
	results: HorarioSeparador[];
	total: { offset: number; limit: number; total_itens: number };
}> => {
	try {
		const params: any = { offset, limit };
		if (search) params.search = search;
		if (tipoSeparador) params.tipo_separador = tipoSeparador;

		const response = await api.get("/horario-separador/search", { params });
		return response.data;
	} catch (error) {
		console.error("Erro ao buscar horários dos operadores:", error);
		throw error;
	}
};


// Buscar horário do operador por ID
export const buscarHorarioSeparadorPorId = async (
	id: number,
): Promise<HorarioSeparador> => {
	try {
		const response = await api.get(`/horario-separador/${id}`);
		return response.data;
	} catch (error) {
		console.error("Erro ao buscar horário do separador:", error);
		throw error;
	}
};


// Criar horário do separador
export const criarHorarioSeparador = async (
	dados: CreateHorarioSeparadorDto,
): Promise<HorarioSeparador> => {
	try {
		const response = await api.post("/horario-separador", dados);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao criar horário do separador:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao criar horário do separador.");
	}
};


// Atualizar horário do separador
export const atualizarHorarioSeparador = async (
	id: number,
	dados: UpdateHorarioSeparadorDto,
): Promise<HorarioSeparador> => {
	try {
		const response = await api.patch(`/horario-separador/${id}`, dados);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao atualizar horário do separador:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao atualizar horário do separador.");
	}
};


// Deletar horário do separador
export const deletarHorarioSeparador = async (id: number): Promise<void> => {
	try {
		await api.delete(`/horario-separador/${id}`);
	} catch (error: any) {
		console.error("Erro ao deletar horário do separador:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao deletar horário do separador.");
	}
};


// Toggle ativo/desativo de horário do separador
export const toggleAtivoHorarioSeparador = async (
	id: number,
): Promise<HorarioSeparador> => {
	try {
		const response = await api.patch(`/horario-separador/${id}/toggle-ativo`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao alterar status do horário do separador:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao alterar status do horário do separador.");
	}
};


// Interface para Canal de Venda
export interface CanalVenda {
	canal_venda_id: number;
	nome: string;
	/** Janela (dias) em que um pedido entregue ainda pode gerar devolução. */
	dias_devolucao?: number;
	horario_corte?: string;
	/**
	 * Quanto menor, maior a prioridade. Usado para ordenação/execução.
	 * Nem todos os ambientes retornam esse campo (por isso é opcional).
	 */
	prioridade?: number;
	/**
	 * #1441: id da conta do Mercado Livre deste canal. Preenchido só nos canais
	 * do ML — é o que habilita a busca do limite de despacho. NULL nos demais.
	 */
	ml_seller_id?: string | null;
	shopee_shop_id?: string | null;
	/**
	 * #1609 — canal é embalado pelo WMS? Só canal marcado entra na fila da
	 * bancada. `true` por padrão: canal novo nasce como sempre foi.
	 */
	embala_no_wms?: boolean;
	/**
	 * #1477: o canal entra na 2ª leva da geração de tarefas — a que roda depois
	 * do portão, com os pedidos que não têm limite de despacho. Só tem efeito
	 * com o macro `restringir-canais-sem-prazo` ligado.
	 */
	gera_sem_limite_despacho?: boolean;
	dias_da_semana?: number[]; // 0 = Domingo, 1 = Segunda, ..., 6 = Sábado
	mesa_separacao?: Array<{
		mesa_separacao_id: number;
		nome: string;
	}>;
}


// Buscar canais de venda com paginação
export const buscarCanaisVendaPaginado = async (params?: {
	search?: string;
	offset?: number;
	limit?: number;
}): Promise<{
	results: CanalVenda[];
	total?: { total_itens: number };
}> => {
	try {
		const queryParams: any = {};
		if (params?.search) queryParams.search = params.search;
		if (params?.offset !== undefined) queryParams.offset = params.offset;
		if (params?.limit !== undefined) queryParams.limit = params.limit;

		const response = await api.get("/canal-venda", { params: queryParams });
		return response.data;
	} catch (error) {
		console.error("Erro ao buscar canais de venda paginado:", error);
		throw error;
	}
};


// Atualizar apenas a janela de devolução (dias) de um canal
export const atualizarDiasDevolucaoCanal = async (
	id: number,
	dias_devolucao: number,
): Promise<CanalVenda> => {
	try {
		const response = await api.patch(`/canal-venda/${id}/dias-devolucao`, {
			dias_devolucao,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao atualizar dias de devolução do canal:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao atualizar dias de devolução do canal.");
	}
};


// #1477: liga/desliga o canal na 2ª leva da geração de tarefas (pedidos sem
// limite de despacho). Só tem efeito com o macro ligado.
export const atualizarGeraSemLimiteDespachoCanal = async (
	id: number,
	gera_sem_limite_despacho: boolean,
): Promise<CanalVenda> => {
	try {
		const response = await api.patch(
			`/canal-venda/${id}/gera-sem-limite-despacho`,
			{ gera_sem_limite_despacho },
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao atualizar geração sem prazo do canal:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao atualizar a geração sem prazo do canal.");
	}
};


// Atualizar canal de venda
export const atualizarCanalVenda = async (
	id: number,
	dados: {
		horario_corte?: string;
		prioridade?: number;
		dias_da_semana?: number[];
		/** #1441: string vazia limpa o vínculo com a conta do ML. */
		ml_seller_id?: string;
		shopee_shop_id?: string;
	},
): Promise<CanalVenda> => {
	try {
		const response = await api.patch(`/canal-venda/${id}`, dados);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao atualizar canal de venda:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao atualizar canal de venda.");
	}
};


// Buscar todos os canais de venda para seleção
export const buscarCanaisVenda = async (): Promise<CanalVenda[]> => {
	try {
		const response = await api.get("/canal-venda");
		// A API retorna { results: [...], total: {...} }
		return response.data.results || response.data;
	} catch (error) {
		console.error("Erro ao buscar canais de venda:", error);
		throw error;
	}
};


// ==================== CRIAÇÃO AUTOMÁTICA DE TAREFAS ====================

export interface CriacaoAutomaticaStatus {
	ativo: boolean;
	ultimaExecucao: Date | null;
	proximaExecucao: string;
}


export interface CriacaoAutomaticaResultado {
	tarefasCriadas: number;
	pedidosProcessados: number;
	erros: string[];
}


/**
 * Ligar o serviço de criação automática de tarefas
 */
export const ligarCriacaoAutomaticaTarefas = async (): Promise<{
	ativo: boolean;
	mensagem: string;
	ultimaExecucao: Date | null;
}> => {
	try {
		const response = await api.post("/tarefa/criacao-automatica/ligar");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao ligar criação automática de tarefas:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao ligar criação automática de tarefas.");
	}
};


/**
 * Desligar o serviço de criação automática de tarefas
 */
export const desligarCriacaoAutomaticaTarefas = async (): Promise<{
	ativo: boolean;
	mensagem: string;
	ultimaExecucao: Date | null;
}> => {
	try {
		const response = await api.post("/tarefa/criacao-automatica/desligar");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao desligar criação automática de tarefas:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao desligar criação automática de tarefas.");
	}
};


/**
 * Verificar o status do serviço de criação automática de tarefas
 */
export const statusCriacaoAutomaticaTarefas =
	async (): Promise<CriacaoAutomaticaStatus> => {
		try {
			const response = await api.get("/tarefa/criacao-automatica/status");
			return response.data;
		} catch (error: any) {
			console.error(
				"Erro ao buscar status da criação automática de tarefas:",
				error,
			);
			if (error.response?.data?.message) {
				throw new Error(error.response.data.message);
			}
			throw new Error(
				"Falha ao buscar status da criação automática de tarefas.",
			);
		}
	};


/**
 * Executar manualmente a criação automática de tarefas
 */
export const executarManualmenteCriacaoAutomaticaTarefas =
	async (): Promise<CriacaoAutomaticaResultado> => {
		try {
			const response = await api.post("/tarefa/criacao-automatica/executar");
			return response.data;
		} catch (error: any) {
			console.error(
				"Erro ao executar manualmente criação automática de tarefas:",
				error,
			);
			if (error.response?.data?.message) {
				throw new Error(error.response.data.message);
			}
			throw new Error(
				"Falha ao executar manualmente criação automática de tarefas.",
			);
		}
	};


// ==================== ATRIBUIÇÃO AUTOMÁTICA DE CONFERÊNCIAS ====================

/**
 * Ligar o serviço de atribuição automática de conferências
 */
export const ligarAtribuicaoAutomaticaConferencias = async (): Promise<{
	ativo: boolean;
	mensagem: string;
	ultimaExecucao: Date | null;
}> => {
	try {
		const response = await api.post(
			"/conferencia-transferencia/atribuicao-automatica/ligar",
		);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao ligar atribuição automática de conferências:",
			error,
		);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao ligar atribuição automática de conferências.");
	}
};


/**
 * Desligar o serviço de atribuição automática de conferências
 */
export const desligarAtribuicaoAutomaticaConferencias = async (): Promise<{
	ativo: boolean;
	mensagem: string;
	ultimaExecucao: Date | null;
}> => {
	try {
		const response = await api.post(
			"/conferencia-transferencia/atribuicao-automatica/desligar",
		);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao desligar atribuição automática de conferências:",
			error,
		);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao desligar atribuição automática de conferências.");
	}
};


/**
 * Verificar o status do serviço de atribuição automática de conferências
 */
export const statusAtribuicaoAutomaticaConferencias =
	async (): Promise<CriacaoAutomaticaStatus> => {
		try {
			const response = await api.get(
				"/conferencia-transferencia/atribuicao-automatica/status",
			);
			return response.data;
		} catch (error: any) {
			console.error(
				"Erro ao buscar status da atribuição automática de conferências:",
				error,
			);
			if (error.response?.data?.message) {
				throw new Error(error.response.data.message);
			}
			throw new Error(
				"Falha ao buscar status da atribuição automática de conferências.",
			);
		}
	};


// ========== IMPORTAÇÃO AUTOMÁTICA DE PRODUTOS DA TINY ==========

export interface ImportacaoAutomaticaStatus {
	ativo: boolean;
	ultimaExecucao: Date | null;
	proximaExecucao: string;
}


export interface ImportacaoAutomaticaResultado {
	sucesso: boolean;
	produtosProcessados?: number;
	erro?: string;
}


// ==================== SCHEDULE (AGENDAMENTOS DO SISTEMA) ====================

export type SystemScheduleType = "cron" | "interval";


export interface SystemScheduleJob {
	job_key: string;
	nome: string;
	descricao?: string | null;
	ativo: boolean;
	tipo_agendamento: SystemScheduleType;
	cron_expressao?: string | null;
	intervalo_segundos?: number | null;
	timezone: string;
	jitter_segundos: number;
	ultima_execucao_em?: string | null;
	ultima_sucesso_em?: string | null;
	ultimo_erro_em?: string | null;
	ultimo_erro_mensagem?: string | null;
	atualizado_em?: string;
	criado_em?: string;
}


export interface UpdateSystemScheduleJobPayload {
	nome?: string;
	descricao?: string | null;
	ativo?: boolean;
	tipo_agendamento?: SystemScheduleType;
	cron_expressao?: string | null;
	intervalo_segundos?: number | null;
	timezone?: string;
	jitter_segundos?: number;
}


export const listarSystemSchedules = async (): Promise<SystemScheduleJob[]> => {
	const response = await api.get("/system-schedules");
	return response.data;
};


export const atualizarSystemSchedule = async (
	jobKey: string,
	payload: UpdateSystemScheduleJobPayload,
): Promise<SystemScheduleJob> => {
	const response = await api.patch(`/system-schedules/${jobKey}`, payload);
	return response.data;
};


export const executarSystemScheduleAgora = async (
	jobKey: string,
): Promise<{ started: boolean; message: string }> => {
	const response = await api.post(`/system-schedules/${jobKey}/run`);
	return response.data;
};


export const cancelarSystemScheduleJob = async (
	jobKey: string,
): Promise<{ cancelled: boolean; message: string }> => {
	const response = await api.post(`/system-schedules/${jobKey}/cancel`);
	return response.data;
};

export const listarJobsEmExecucao = async (): Promise<
	Array<{ jobKey: string; slug: string | null }>
> => {
	const response = await api.get("/system-schedules/running/list");
	return response.data;
};

export const seedSystemSchedules = async (): Promise<{ ok: boolean }> => {
	const response = await api.post("/system-schedules/seed-defaults");
	return response.data;
};


// ---------- DEAD-LETTER DO OUTBOX (integração Gestão) ----------

export interface OutboxDeadItem {
	id: string;
	event_type: string;
	attempts: number;
	last_error: string | null;
	/** Quando o evento entrou na fila (a tabela não guarda quando morreu). */
	created_at: string;
	/** Só `estoque.movimento` sai do dead-letter pelo painel. */
	reenviavel: boolean;
}

export interface OutboxDeadResumo {
	total: number;
	reenviaveis: number;
	/** Os mais recentes, até 100. */
	itens: OutboxDeadItem[];
}

export const listarOutboxDead = async (): Promise<OutboxDeadResumo> => {
	const response = await api.get("/integracao-gestao/outbox/dead");
	return response.data;
};

export const reenviarOutboxDead = async (): Promise<{ reenviados: number }> => {
	const response = await api.post("/integracao-gestao/outbox/dead/reenviar");
	return response.data;
};


// ==================== CONFIGURAÇÕES EDITÁVEIS DO SISTEMA ====================

export interface ReposicaoMinimoPorArmazemRow {
	armazem_id: number;
	armazem_nome: string;
	minimo_produtos: number;
}


export const salvarReposicaoMinimosPorArmazem = async (
	itens: Array<{ armazem_id: number; minimo_produtos: number }>,
): Promise<{ updated: number }> => {
	const response = await api.put("/system-settings/reposicao-minimos", {
		itens,
	});
	return response.data;
};


export const buscarCapacidadePrateleira = async (): Promise<{
	capacidade_prateleira: number;
}> => {
	const response = await api.get("/system-settings/capacidade-prateleira");
	return response.data;
};


export const salvarCapacidadePrateleira = async (
	capacidade_prateleira: number,
): Promise<{
	capacidade_prateleira: number;
}> => {
	const response = await api.put("/system-settings/capacidade-prateleira", {
		capacidade_prateleira,
	});
	return response.data;
};


export const buscarCapacidadeCesto = async (): Promise<{
	capacidade_cesto: number;
}> => {
	const response = await api.get("/system-settings/capacidade-cesto");
	return response.data;
};


export const salvarCapacidadeCesto = async (
	capacidade_cesto: number,
): Promise<{
	capacidade_cesto: number;
}> => {
	const response = await api.put("/system-settings/capacidade-cesto", {
		capacidade_cesto,
	});
	return response.data;
};


export const buscarMaxPedidosPorTarefaAutomatica = async (): Promise<{
	max_pedidos_por_tarefa_automatica: number;
}> => {
	const response = await api.get(
		"/system-settings/max-pedidos-por-tarefa-automatica",
	);
	return response.data;
};


export const buscarMinPedidosPorTarefaAutomatica = async (): Promise<{
	min_pedidos_por_tarefa_automatica: number;
}> => {
	const response = await api.get(
		"/system-settings/min-pedidos-por-tarefa-automatica",
	);
	return response.data;
};


export const salvarMaxPedidosPorTarefaAutomatica = async (
	max_pedidos_por_tarefa_automatica: number,
): Promise<{
	max_pedidos_por_tarefa_automatica: number;
}> => {
	const response = await api.put(
		"/system-settings/max-pedidos-por-tarefa-automatica",
		{
			max_pedidos_por_tarefa_automatica,
		},
	);
	return response.data;
};


export const salvarMinPedidosPorTarefaAutomatica = async (
	min_pedidos_por_tarefa_automatica: number,
): Promise<{
	min_pedidos_por_tarefa_automatica: number;
}> => {
	const response = await api.put(
		"/system-settings/min-pedidos-por-tarefa-automatica",
		{
			min_pedidos_por_tarefa_automatica,
		},
	);
	return response.data;
};


export interface HorarioManutencao {
	horario_inicio: string;
	horario_fim: string;
	/**
	 * Timezone opcional (ex.: America/Sao_Paulo).
	 * Alguns ambientes podem não retornar esse campo.
	 */
	timezone?: string;
}


// Nome legado (mantido por compatibilidade)
export type HorarioBloqueioCriacaoAutomatica = HorarioManutencao;


export const buscarHorarioBloqueioCriacaoAutomatica =
	async (): Promise<HorarioBloqueioCriacaoAutomatica> => {
		const response = await api.get(
			"/system-settings/horario-bloqueio-criacao-automatica",
		);
		return response.data;
	};


export const salvarHorarioBloqueioCriacaoAutomatica = async (
	horario_inicio: string,
	horario_fim: string,
): Promise<HorarioBloqueioCriacaoAutomatica> => {
	const response = await api.put(
		"/system-settings/horario-bloqueio-criacao-automatica",
		{ horario_inicio, horario_fim },
	);
	return response.data;
};


// Alias com nome mais claro (usado na tela de Parâmetros)
export const buscarHorarioManutencao = async (): Promise<HorarioManutencao> => {
	try {
		const response = await api.get("/system-settings/horario-manutencao");
		return response.data;
	} catch {
		// Fallback para endpoint legado em ambientes não migrados.
		return buscarHorarioBloqueioCriacaoAutomatica();
	}
};


export const salvarHorarioManutencao = async (
	horario_inicio: string,
	horario_fim: string,
): Promise<HorarioManutencao> => {
	try {
		const response = await api.put("/system-settings/horario-manutencao", {
			horario_inicio,
			horario_fim,
		});
		return response.data;
	} catch {
		// Fallback para endpoint legado em ambientes não migrados.
		return salvarHorarioBloqueioCriacaoAutomatica(horario_inicio, horario_fim);
	}
};


export const buscarArmazensIgnoradosSeparacao = async (): Promise<{
	armazem_ids: number[];
}> => {
	const response = await api.get(
		"/system-settings/armazens-ignorados-separacao",
	);
	return response.data;
};


export const salvarArmazensIgnoradosSeparacao = async (
	armazem_ids: number[],
): Promise<{
	armazem_ids: number[];
}> => {
	const response = await api.put(
		"/system-settings/armazens-ignorados-separacao",
		{ armazem_ids },
	);
	return response.data;
};


export const buscarArmazensIgnoradosFull = async (): Promise<{
	armazem_ids: number[];
}> => {
	const response = await api.get("/system-settings/armazens-ignorados-full");
	return response.data;
};


export const salvarArmazensIgnoradosFull = async (
	armazem_ids: number[],
): Promise<{
	armazem_ids: number[];
}> => {
	const response = await api.put("/system-settings/armazens-ignorados-full", {
		armazem_ids,
	});
	return response.data;
};


export const buscarLocalizacoesIgnoradasSeparacaoPedido = async (): Promise<{
	localizacao_ids: number[];
}> => {
	const response = await api.get(
		"/system-settings/localizacoes-ignoradas-separacao-pedido",
	);
	return response.data;
};


export const salvarLocalizacoesIgnoradasSeparacaoPedido = async (
	localizacao_ids: number[],
): Promise<{
	localizacao_ids: number[];
}> => {
	const response = await api.put(
		"/system-settings/localizacoes-ignoradas-separacao-pedido",
		{ localizacao_ids },
	);
	return response.data;
};


export const buscarLocalizacoesIgnoradasFull = async (): Promise<{
	localizacao_ids: number[];
}> => {
	const response = await api.get("/system-settings/localizacoes-ignoradas-full");
	return response.data;
};


export const salvarLocalizacoesIgnoradasFull = async (
	localizacao_ids: number[],
): Promise<{
	localizacao_ids: number[];
}> => {
	const response = await api.put("/system-settings/localizacoes-ignoradas-full", {
		localizacao_ids,
	});
	return response.data;
};


// ==================== MOBILE (VISIBILIDADE DE MÓDULOS) ====================

export interface MobileMovimentacaoConfig {
	habilitado: boolean;
	operador_ids: number[];
}


export const buscarMobileMovimentacaoConfig =
	async (): Promise<MobileMovimentacaoConfig> => {
		const response = await api.get("/system-settings/mobile-movimentacao");
		return response.data;
	};


export const salvarMobileMovimentacaoConfig = async (
	habilitado: boolean,
	operador_ids: number[],
): Promise<MobileMovimentacaoConfig> => {
	const response = await api.put("/system-settings/mobile-movimentacao", {
		habilitado,
		operador_ids,
	});
	return response.data;
};


// ==================== FILA DE SYNC DE PEDIDOS ====================

export interface SyncFilaConfig {
	habilitado: boolean;
}

export const buscarSyncFilaConfig = async (): Promise<SyncFilaConfig> => {
	const response = await api.get("/system-settings/sync-fila");
	return response.data;
};

export const salvarSyncFilaConfig = async (
	habilitado: boolean,
): Promise<SyncFilaConfig> => {
	const response = await api.put("/system-settings/sync-fila", { habilitado });
	return response.data;
};

// ==================== FATURAMENTO AUTOMÁTICO (#1746) ====================

export interface FaturamentoAutomaticoConfig {
	habilitado: boolean;
}

export const buscarFaturamentoAutomatico =
	async (): Promise<FaturamentoAutomaticoConfig> => {
		const response = await api.get("/system-settings/faturamento-automatico");
		return response.data;
	};

export const salvarFaturamentoAutomatico = async (
	habilitado: boolean,
): Promise<FaturamentoAutomaticoConfig> => {
	const response = await api.put("/system-settings/faturamento-automatico", {
		habilitado,
	});
	return response.data;
};

// ==================== AUTO-VÍNCULO DE NOTAS (REMESSA↔TRANSFERÊNCIA) ====================

export interface AutoVinculoConfig {
	habilitado: boolean;
}

export const buscarAutoVinculoConfig = async (): Promise<AutoVinculoConfig> => {
	const response = await api.get("/system-settings/auto-vinculo");
	return response.data;
};

export const salvarAutoVinculoConfig = async (
	habilitado: boolean,
): Promise<AutoVinculoConfig> => {
	const response = await api.put("/system-settings/auto-vinculo", { habilitado });
	return response.data;
};

/** #1901: bloqueio de operar remessa sem vínculo. Default ligado. */
export interface BloqueioRemessaSemVinculoConfig {
	habilitado: boolean;
}

export const buscarBloqueioRemessaSemVinculo =
	async (): Promise<BloqueioRemessaSemVinculoConfig> => {
		const response = await api.get(
			"/system-settings/bloqueio-remessa-sem-vinculo",
		);
		return response.data;
	};

export const salvarBloqueioRemessaSemVinculo = async (
	habilitado: boolean,
): Promise<BloqueioRemessaSemVinculoConfig> => {
	const response = await api.put(
		"/system-settings/bloqueio-remessa-sem-vinculo",
		{ habilitado },
	);
	return response.data;
};

// ==================== ORIGEM DA OCORRÊNCIA (#1855) ====================

/**
 * Por qual nota a ocorrência de conferência nasce. Espelha o enum
 * `ClasseEntrada` do backend, que é a fonte da regra.
 */
export type OrigemOcorrencia = "TRANSFERENCIA" | "COMPRA";

export interface OrigemOcorrenciaConfig {
	origem: OrigemOcorrencia;
}

export const buscarOrigemOcorrenciaConfig =
	async (): Promise<OrigemOcorrenciaConfig> => {
		const response = await api.get("/system-settings/origem-ocorrencia");
		return response.data;
	};

export const salvarOrigemOcorrenciaConfig = async (
	origem: OrigemOcorrencia,
): Promise<OrigemOcorrenciaConfig> => {
	const response = await api.put("/system-settings/origem-ocorrencia", {
		origem,
	});
	return response.data;
};

export type DashboardNotasClasse = "COMPRA" | "TRANSFERENCIA";

export interface DashboardNotasClassesConfig {
	classes: DashboardNotasClasse[];
}

export const buscarDashboardNotasClasses =
	async (): Promise<DashboardNotasClassesConfig> => {
		const response = await api.get("/system-settings/dashboard-notas-classes");
		return response.data;
	};

export const salvarDashboardNotasClasses = async (
	classes: DashboardNotasClasse[],
): Promise<DashboardNotasClassesConfig> => {
	const response = await api.put("/system-settings/dashboard-notas-classes", {
		classes,
	});
	return response.data;
};


// ==================== SÓ DESPACHO HOJE (RN-DESP-04, #1441) ====================

export interface SomenteDespachoHojeConfig {
	habilitado: boolean;
}

export const buscarSomenteDespachoHojeConfig =
	async (): Promise<SomenteDespachoHojeConfig> => {
		const response = await api.get("/system-settings/somente-despacho-hoje");
		return response.data;
	};

export const salvarSomenteDespachoHojeConfig = async (
	habilitado: boolean,
): Promise<SomenteDespachoHojeConfig> => {
	const response = await api.put("/system-settings/somente-despacho-hoje", {
		habilitado,
	});
	return response.data;
};


// ============ FATURAMENTO AUTOMÁTICO DO FULL PELO ML (#1886) ============

export type GatilhoFaturamentoFullMl = "recebimento" | "nota";

/**
 * `desligado`: só registra o aviso. `sinalizar`: a tela do Full mostra que o ML
 * recebeu e o operador fatura. `faturar`: o WMS fatura sozinho.
 */
export type ModoFaturamentoFullMl = "desligado" | "sinalizar" | "faturar";

export interface FaturamentoAutomaticoFullMlConfig {
	modo: ModoFaturamentoFullMl;
	/** Derivado de `modo === "faturar"`. */
	habilitado: boolean;
	gatilho: GatilhoFaturamentoFullMl;
	/** Responsável pelas saídas: quem ligou a chave. */
	usuario_id: number | null;
	/** Só Fulls incluídos depois disto são faturados sozinhos. */
	habilitado_em: string | null;
}

export const buscarFaturamentoAutomaticoFullMlConfig =
	async (): Promise<FaturamentoAutomaticoFullMlConfig> => {
		const response = await api.get(
			"/system-settings/faturamento-automatico-full-ml",
		);
		return response.data;
	};

export const salvarFaturamentoAutomaticoFullMlConfig = async (dados: {
	modo: ModoFaturamentoFullMl;
	gatilho: GatilhoFaturamentoFullMl;
	/** #1986: desde quando o "faturar sozinho" vale. Sem ele, desde agora. */
	valer_desde?: string;
}): Promise<FaturamentoAutomaticoFullMlConfig> => {
	const response = await api.put(
		"/system-settings/faturamento-automatico-full-ml",
		dados,
	);
	return response.data;
};

// ============ FULL — LANÇAR NA TINY AO FATURAR (#1958) ============

/**
 * Ligado: faturar o Full também movimenta a Tiny (débito do pedido, entrada no
 * depósito do Full, carimbo de entregue). Desligado: só no WMS.
 */
export interface FaturamentoFullTinyConfig {
	habilitado: boolean;
}

export const buscarFaturamentoFullTinyConfig =
	async (): Promise<FaturamentoFullTinyConfig> => {
		const response = await api.get("/system-settings/faturamento-full-tiny");
		return response.data;
	};

export const salvarFaturamentoFullTinyConfig = async (
	habilitado: boolean,
): Promise<FaturamentoFullTinyConfig> => {
	const response = await api.put("/system-settings/faturamento-full-tiny", {
		habilitado,
	});
	return response.data;
};


// ============ PORTÃO DA GERAÇÃO (#1477) ============

export interface TravarGeracaoDiaConfig {
	habilitado: boolean;
}

export const buscarTravarGeracaoDiaConfig =
	async (): Promise<TravarGeracaoDiaConfig> => {
		const response = await api.get("/system-settings/travar-geracao-dia");
		return response.data;
	};

export const salvarTravarGeracaoDiaConfig = async (
	habilitado: boolean,
): Promise<TravarGeracaoDiaConfig> => {
	const response = await api.put("/system-settings/travar-geracao-dia", {
		habilitado,
	});
	return response.data;
};

export interface MontarNaAtribuicaoConfig {
	habilitado: boolean;
}

export const buscarMontarNaAtribuicaoConfig =
	async (): Promise<MontarNaAtribuicaoConfig> => {
		const response = await api.get("/system-settings/montar-na-atribuicao");
		return response.data;
	};

export const salvarMontarNaAtribuicaoConfig = async (
	habilitado: boolean,
): Promise<MontarNaAtribuicaoConfig> => {
	const response = await api.put("/system-settings/montar-na-atribuicao", {
		habilitado,
	});
	return response.data;
};


// ============ RESTRINGIR CANAIS SEM PRAZO (2ª leva, #1477) ============

export interface RestringirCanaisSemPrazoConfig {
	habilitado: boolean;
}

export const buscarRestringirCanaisSemPrazoConfig =
	async (): Promise<RestringirCanaisSemPrazoConfig> => {
		const response = await api.get(
			"/system-settings/restringir-canais-sem-prazo",
		);
		return response.data;
	};

export const salvarRestringirCanaisSemPrazoConfig = async (
	habilitado: boolean,
): Promise<RestringirCanaisSemPrazoConfig> => {
	const response = await api.put(
		"/system-settings/restringir-canais-sem-prazo",
		{ habilitado },
	);
	return response.data;
};


// ==================== SYNC AJUSTE → TINY ====================

export interface AjusteTinyConfig {
	habilitado: boolean;
	gap_maximo: number;
	pular_se_reserva_wms: boolean;
	pular_se_reserva_tiny: boolean;
	deposito: string;
}

export const buscarAjusteTinyConfig = async (): Promise<AjusteTinyConfig> => {
	const response = await api.get("/system-settings/ajuste-tiny");
	return response.data;
};

export const salvarAjusteTinyConfig = async (
	config: Partial<AjusteTinyConfig>,
): Promise<AjusteTinyConfig> => {
	const response = await api.put("/system-settings/ajuste-tiny", config);
	return response.data;
};

// ============ LANÇAMENTO AUTOMÁTICO NA TINY (#1656) ====================

export interface EntradaTinyConfig {
	habilitado: boolean;
	adotar_orfas: boolean;
	janela_adocao_horas: number;
	max_tentativas: number;
}

export const buscarEntradaTinyConfig = async (): Promise<EntradaTinyConfig> => {
	const response = await api.get("/system-settings/entrada-tiny");
	return response.data;
};

export const salvarEntradaTinyConfig = async (
	config: Partial<EntradaTinyConfig>,
): Promise<EntradaTinyConfig> => {
	const response = await api.put("/system-settings/entrada-tiny", config);
	return response.data;
};


// ==================== FONTE DO CADASTRO DE PRODUTO ====================

export type FonteCadastroProduto = "tiny" | "comparar" | "gestao";

export const buscarFonteCadastroProduto = async (): Promise<{
	fonte: FonteCadastroProduto;
}> => {
	const response = await api.get("/system-settings/produto-fonte-cadastro");
	return response.data;
};

export const salvarFonteCadastroProduto = async (
	fonte: FonteCadastroProduto,
): Promise<{ fonte: FonteCadastroProduto }> => {
	const response = await api.put("/system-settings/produto-fonte-cadastro", {
		fonte,
	});
	return response.data;
};

// ==================== PRAZO DE CONFERÊNCIA ====================

export interface PrazoConferenciaConfig {
	prazo_maximo_dias: number;
}

export const buscarPrazoConferencia = async (): Promise<PrazoConferenciaConfig> => {
	const response = await api.get("/system-settings/prazo-conferencia");
	return response.data;
};

export const salvarPrazoConferencia = async (
	prazo_maximo_dias: number,
): Promise<PrazoConferenciaConfig> => {
	const response = await api.put("/system-settings/prazo-conferencia", {
		prazo_maximo_dias,
	});
	return response.data;
};


// ==================== RETENÇÃO DE ETIQUETA ====================

export interface RetencaoEtiquetaConfig {
	retencao_dias: number;
}

export const buscarRetencaoEtiqueta = async (): Promise<RetencaoEtiquetaConfig> => {
	const response = await api.get("/system-settings/retencao-etiqueta");
	return response.data;
};

export const salvarRetencaoEtiqueta = async (
	retencao_dias: number,
): Promise<RetencaoEtiquetaConfig> => {
	const response = await api.put("/system-settings/retencao-etiqueta", {
		retencao_dias,
	});
	return response.data;
};


// ==================== PRIORIDADE TIPO DE LOCALIZAÇÃO ====================

export const buscarPrioridadeTipoLocalizacao = async (): Promise<{
	tipos: string[];
}> => {
	const response = await api.get(
		"/system-settings/prioridade-tipo-localizacao",
	);
	return response.data;
};


export const salvarPrioridadeTipoLocalizacao = async (
	tipos: string[],
): Promise<{ tipos: string[] }> => {
	const response = await api.put(
		"/system-settings/prioridade-tipo-localizacao",
		{ tipos },
	);
	return response.data;
};


export const buscarPrioridadeTipoLocalizacaoPedido = async (): Promise<{
	tipos: string[];
}> => {
	const response = await api.get(
		"/system-settings/prioridade-tipo-localizacao-pedido",
	);
	return response.data;
};


export const salvarPrioridadeTipoLocalizacaoPedido = async (
	tipos: string[],
): Promise<{ tipos: string[] }> => {
	const response = await api.put(
		"/system-settings/prioridade-tipo-localizacao-pedido",
		{ tipos },
	);
	return response.data;
};


export const buscarPrioridadeTipoLocalizacaoFull = async (): Promise<{
	tipos: string[];
}> => {
	const response = await api.get(
		"/system-settings/prioridade-tipo-localizacao-full",
	);
	return response.data;
};


export const salvarPrioridadeTipoLocalizacaoFull = async (
	tipos: string[],
): Promise<{ tipos: string[] }> => {
	const response = await api.put(
		"/system-settings/prioridade-tipo-localizacao-full",
		{ tipos },
	);
	return response.data;
};


// ==================== ROTA DE BIPAGEM (ORDEM FÍSICA DO PERCURSO) ====================

/**
 * Mapa físico das ruas por armazém + tipo de localização.
 * `{ "Dib Jorge": { "prateleira": ["A", "B", ...] } }` — a posição no array é a
 * posição real no galpão. Armazém sem cadastro segue ordem alfabética.
 */
export interface RotaMapaRuas {
	armazens: Record<string, Record<string, string[]>>;
}


export const buscarRotaMapaRuas = async (): Promise<RotaMapaRuas> => {
	const response = await api.get("/system-settings/rota-mapa-ruas");
	return response.data;
};


export const salvarRotaMapaRuas = async (
	armazens: Record<string, Record<string, string[]>>,
): Promise<RotaMapaRuas> => {
	const response = await api.put("/system-settings/rota-mapa-ruas", {
		armazens,
	});
	return response.data;
};


export const buscarRotaOrdemTipoLocalizacao = async (): Promise<{
	tipos: string[];
}> => {
	const response = await api.get(
		"/system-settings/rota-ordem-tipo-localizacao",
	);
	return response.data;
};


export const salvarRotaOrdemTipoLocalizacao = async (
	tipos: string[],
): Promise<{ tipos: string[] }> => {
	const response = await api.put(
		"/system-settings/rota-ordem-tipo-localizacao",
		{ tipos },
	);
	return response.data;
};


// ==================== PRIORIDADE TIPO DE ORIGEM (REPOSIÇÃO INTERNA) ====================

export const buscarPrioridadeTipoOrigem = async (): Promise<{
	tipos: string[];
}> => {
	const response = await api.get(
		"/system-settings/reposicao/prioridade-tipo-origem",
	);
	return response.data;
};


export const salvarPrioridadeTipoOrigem = async (
	tipos: string[],
): Promise<{ tipos: string[] }> => {
	const response = await api.put(
		"/system-settings/reposicao/prioridade-tipo-origem",
		{ tipos },
	);
	return response.data;
};


// ==================== CAPACIDADE POR TIPO (FASE 1 REMANEJAMENTO) ====================

export const buscarCapacidadePorTipo = async (): Promise<{
	capacidades: Record<string, number>;
}> => {
	const response = await api.get(
		"/system-settings/reposicao/capacidade-por-tipo",
	);
	return response.data;
};


export const salvarCapacidadePorTipo = async (
	capacidades: Record<string, number>,
): Promise<{ capacidades: Record<string, number> }> => {
	const response = await api.put(
		"/system-settings/reposicao/capacidade-por-tipo",
		{ capacidades },
	);
	return response.data;
};


// ==================== BLOQUEIOS DE ORIGEM (FASE 1 REMANEJAMENTO) ====================

export const buscarTiposOrigemBloqueadosInterna = async (): Promise<{
	tipos: string[];
}> => {
	const response = await api.get(
		"/system-settings/reposicao/tipos-origem-bloqueados-interna",
	);
	return response.data;
};


export const salvarTiposOrigemBloqueadosInterna = async (
	tipos: string[],
): Promise<{ tipos: string[] }> => {
	const response = await api.put(
		"/system-settings/reposicao/tipos-origem-bloqueados-interna",
		{ tipos },
	);
	return response.data;
};


export const buscarTiposOrigemBloqueadosPedido = async (): Promise<{
	tipos: string[];
}> => {
	const response = await api.get(
		"/system-settings/reposicao/tipos-origem-bloqueados-pedido",
	);
	return response.data;
};


export const salvarTiposOrigemBloqueadosPedido = async (
	tipos: string[],
): Promise<{ tipos: string[] }> => {
	const response = await api.put(
		"/system-settings/reposicao/tipos-origem-bloqueados-pedido",
		{ tipos },
	);
	return response.data;
};


/**
 * Ligar o serviço de importação automática de produtos da Tiny
 */
export const ligarImportacaoAutomaticaProdutos = async (): Promise<{
	ativo: boolean;
	mensagem: string;
	ultimaExecucao: Date | null;
}> => {
	try {
		const response = await api.post("/produto/importacao-automatica/ligar");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao ligar importação automática de produtos:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao ligar importação automática de produtos.");
	}
};


/**
 * Desligar o serviço de importação automática de produtos da Tiny
 */
export const desligarImportacaoAutomaticaProdutos = async (): Promise<{
	ativo: boolean;
	mensagem: string;
	ultimaExecucao: Date | null;
}> => {
	try {
		const response = await api.post("/produto/importacao-automatica/desligar");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao desligar importação automática de produtos:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao desligar importação automática de produtos.");
	}
};


/**
 * Verificar o status do serviço de importação automática de produtos
 */
export const statusImportacaoAutomaticaProdutos =
	async (): Promise<ImportacaoAutomaticaStatus> => {
		try {
			const response = await api.get("/produto/importacao-automatica/status");
			return response.data;
		} catch (error: any) {
			console.error(
				"Erro ao buscar status da importação automática de produtos:",
				error,
			);
			if (error.response?.data?.message) {
				throw new Error(error.response.data.message);
			}
			throw new Error(
				"Falha ao buscar status da importação automática de produtos.",
			);
		}
	};


/**
 * Executar manualmente a importação automática de produtos da Tiny
 */
export const executarManualmenteImportacaoAutomaticaProdutos = async (
	token?: string,
): Promise<ImportacaoAutomaticaResultado> => {
	try {
		const response = await api.post("/produto/importacao-automatica/executar", {
			token,
		});
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao executar manualmente importação automática de produtos:",
			error,
		);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error(
			"Falha ao executar manualmente importação automática de produtos.",
		);
	}
};


/**
 * Sincronizar marcas de 10 produtos com a Tiny
 */
export const sincronizarMarcasProdutos = async (
	token?: string,
): Promise<ImportacaoAutomaticaResultado> => {
	try {
		const response = await api.post("/produto/sincronizar-marcas", {
			token,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao sincronizar marcas de produtos:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao sincronizar marcas de produtos.");
	}
};


// ========== CRIAÇÃO AUTOMÁTICA DE REPOSIÇÃO DE PEDIDOS ==========

export interface CriacaoAutomaticaReposicaoPedidoResultado {
	tarefasCriadas: number;
	reposicoesProcessadas: number;
	erros: string[];
}


/**
 * Ligar o serviço de criação automática de tarefas de reposição de pedidos
 */
export const ligarCriacaoAutomaticaReposicaoPedido = async (): Promise<{
	ativo: boolean;
	mensagem: string;
	ultimaExecucao: Date | null;
}> => {
	try {
		const response = await api.post(
			"/tarefa/criacao-automatica-reposicao-pedido/ligar",
		);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao ligar criação automática de reposição de pedidos:",
			error,
		);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error(
			"Falha ao ligar criação automática de reposição de pedidos.",
		);
	}
};


/**
 * Desligar o serviço de criação automática de tarefas de reposição de pedidos
 */
export const desligarCriacaoAutomaticaReposicaoPedido = async (): Promise<{
	ativo: boolean;
	mensagem: string;
	ultimaExecucao: Date | null;
}> => {
	try {
		const response = await api.post(
			"/tarefa/criacao-automatica-reposicao-pedido/desligar",
		);
		return response.data;
	} catch (error: any) {
		console.error(
			"Erro ao desligar criação automática de reposição de pedidos:",
			error,
		);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error(
			"Falha ao desligar criação automática de reposição de pedidos.",
		);
	}
};


/**
 * Verificar o status do serviço de criação automática de tarefas de reposição de pedidos
 */
export const statusCriacaoAutomaticaReposicaoPedido =
	async (): Promise<CriacaoAutomaticaStatus> => {
		try {
			const response = await api.get(
				"/tarefa/criacao-automatica-reposicao-pedido/status",
			);
			return response.data;
		} catch (error: any) {
			console.error(
				"Erro ao buscar status da criação automática de reposição de pedidos:",
				error,
			);
			if (error.response?.data?.message) {
				throw new Error(error.response.data.message);
			}
			throw new Error(
				"Falha ao buscar status da criação automática de reposição de pedidos.",
			);
		}
	};


/**
 * Executar manualmente a criação automática de tarefas de reposição de pedidos
 */
export const executarManualmenteCriacaoAutomaticaReposicaoPedido =
	async (): Promise<CriacaoAutomaticaReposicaoPedidoResultado> => {
		try {
			const response = await api.post(
				"/tarefa/criacao-automatica-reposicao-pedido/executar",
			);
			return response.data;
		} catch (error: any) {
			console.error(
				"Erro ao executar manualmente criação automática de reposição de pedidos:",
				error,
			);
			if (error.response?.data?.message) {
				throw new Error(error.response.data.message);
			}
			throw new Error(
				"Falha ao executar manualmente criação automática de reposição de pedidos.",
			);
		}
	};

// ========== ENTRADA ESTOQUE ALOCAÇÃO (POR_ITEM) ==========

export interface EntradaEstoqueAlocacaoConfig {
	modo: string;
}

export const buscarEntradaEstoqueAlocacao =
	async (): Promise<EntradaEstoqueAlocacaoConfig> => {
		const response = await api.get(
			"/system-settings/entrada-estoque-alocacao",
		);
		return response.data;
	};

export const salvarEntradaEstoqueAlocacao = async (
	modo: string,
): Promise<EntradaEstoqueAlocacaoConfig> => {
	const response = await api.put(
		"/system-settings/entrada-estoque-alocacao",
		{ modo },
	);
	return response.data;
};

// ========== MODO DE RECONFERÊNCIA ==========

export interface ReconferenciaModoConfig {
	modo: string;
}

export const buscarReconferenciaModo = async (): Promise<ReconferenciaModoConfig> => {
	const response = await api.get("/system-settings/reconferencia-modo");
	return response.data;
};

export const salvarReconferenciaModo = async (
	modo: string,
): Promise<ReconferenciaModoConfig> => {
	const response = await api.put("/system-settings/reconferencia-modo", { modo });
	return response.data;
};

